import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { formatLiveInput } from "@/lib/format";
import { getAnimal } from "@/lib/animals";
import { useData } from "@/context/DataContext";

export function CurrencyInput({ value, onChange, ...props }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">Rp</span>
      <Input
        {...props}
        inputMode="decimal"
        className="pl-9"
        value={value}
        onChange={(e) => onChange(formatLiveInput(e.target.value))}
      />
    </div>
  );
}

export function Fab({ onClick, testId, label = "Tambah" }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="fixed bottom-8 right-8 z-40 flex items-center gap-2 rounded-full bg-primary px-5 py-4 text-primary-foreground shadow-[0_10px_30px_rgba(0,0,0,0.25)] transition-transform hover:-translate-y-1 active:scale-95"
    >
      <Plus className="h-5 w-5" />
      <span className="hidden text-sm font-semibold sm:inline">{label}</span>
    </button>
  );
}

export function StickyTotal({ children, testId }) {
  return (
    <div
      data-testid={testId}
      className="sticky top-[64px] z-20 -mx-4 mb-4 border-b border-border bg-background/85 px-4 py-4 backdrop-blur-xl md:-mx-8 md:px-8"
    >
      {children}
    </div>
  );
}

export function ConfirmDelete({ onConfirm, trigger, title = "Hapus data ini?", description = "Tindakan ini tidak dapat dibatalkan. Data akan dihapus permanen.", testId }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent data-testid={testId ? `${testId}-dialog` : undefined}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={testId ? `${testId}-cancel` : undefined}>Batal</AlertDialogCancel>
          <AlertDialogAction data-testid={testId ? `${testId}-confirm` : undefined} onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function RowActions({ onEdit, onDelete, id }) {
  return (
    <div className="flex items-center justify-end gap-1">
      <Button data-testid={`edit-${id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit}>
        <Pencil className="h-4 w-4" />
      </Button>
      <ConfirmDelete
        testId={`delete-${id}`}
        onConfirm={onDelete}
        trigger={
          <Button data-testid={`delete-${id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600">
            <Trash2 className="h-4 w-4" />
          </Button>
        }
      />
    </div>
  );
}

export function EmptyState({ text = "Belum ada data. Klik tombol Tambah." }) {
  return <div className="py-16 text-center text-sm text-muted-foreground">{text}</div>;
}

// Show animal emoji for a given side (Pria/Wanita)
export function SideIcon({ side, className = "" }) {
  const { pasanganInfo } = useData();
  let animalKey = side === "Pria" ? pasanganInfo?.pria?.animal : pasanganInfo?.wanita?.animal;
  const emoji = animalKey ? getAnimal(animalKey).emoji : side === "Pria" ? "\uD83E\uDD35" : "\uD83D\uDC70";
  return <span className={className} title={side}>{emoji}</span>;
}

export function PageTitle({ title, subtitle, desc, icon: Icon }) {
  return (
    <div className="mb-6 flex items-start gap-3">
      {Icon && <div className="rounded-lg bg-primary/10 p-2 text-primary"><Icon className="h-5 w-5" /></div>}
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
        {(subtitle || desc) && <p className="mt-1 text-sm text-muted-foreground">{subtitle || desc}</p>}
      </div>
    </div>
  );
}
